import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, AlertTriangle, RefreshCw, ArrowRight, Loader2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * R233 (2026-10-20, user request "KESIAPAN KONFIGURASI KEHADIRAN"): panel
 * "Kesiapan Absensi" di tab Pengaturan — 7 pemeriksaan dari data nyata
 * (endpoint /attendance/readiness). Status: Siap / Belum Lengkap / Perlu
 * Diperbaiki. Ringkasan keseluruhan TIDAK pernah "Siap Digunakan" selama ada
 * cek yang belum ready. Read-only users melihat masalah + hint kontak; tombol
 * perbaikan hanya untuk pemegang akses konfigurasi (canWrite).
 */
const STATUS_META = {
  ready: { label: "Siap", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: ShieldCheck },
  incomplete: { label: "Belum Lengkap", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: AlertTriangle },
  needs_fix: { label: "Perlu Diperbaiki", cls: "bg-rose-50 text-rose-700 border-rose-300", Icon: ShieldAlert },
};

// R234: format tanggal Indonesia penuh dari tanggal acuan kanonis backend
// ("2026-10-19" → "19 Oktober 2026"). Frontend TIDAK menghitung "hari ini"
// sendiri — selalu menampilkan effective_date dari backend. WAJIB pakai
// timeZone "Asia/Jakarta" saat merender: tanpa itu, browser di zona lain
// (mis. UTC) akan menggeser tanggal sehari ke belakang.
function fmtTanggalID(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(`${isoDate}T00:00:00+07:00`);
  if (isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" });
}

export default function KesiapanPanel({ canWrite, onGoSection, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/attendance/readiness");
      setData(r.data);
    } catch (e) {
      setError(formatDetail(e.response?.data?.detail) || "Gagal memuat kesiapan absensi.");
    } finally { setLoading(false); }
  }, []);
  // refreshKey berubah saat user berpindah sub-bagian pengaturan → status
  // kesiapan ikut diperbarui otomatis setelah konfigurasi dilengkapi.
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <section className="border border-slate-200 bg-white" data-testid="readiness-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Kesiapan Absensi</h3>
        <button type="button" onClick={load} data-testid="readiness-refresh"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]">
          <RefreshCw size={11} /> Muat Ulang
        </button>
      </div>

      {loading ? (
        <div className="px-3 py-6 text-center text-[11px] text-slate-500" data-testid="readiness-loading">
          <Loader2 size={14} className="inline animate-spin mr-1" /> Memeriksa konfigurasi…
        </div>
      ) : error ? (
        <div className="px-3 py-6 text-center space-y-2" data-testid="readiness-error">
          <div className="text-[11px] text-rose-700">{error}</div>
          <Button type="button" size="sm" variant="outline" onClick={load} className="rounded-none text-xs" data-testid="readiness-retry">
            <RefreshCw size={12} className="mr-1" /> Coba Lagi
          </Button>
        </div>
      ) : !data ? (
        <div className="px-3 py-6 text-center text-[11px] text-slate-400 italic" data-testid="readiness-empty">Belum ada data kesiapan.</div>
      ) : (
        <>
          <div className={`px-3 py-2 flex items-center gap-2 border-b ${data.overall_ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="readiness-summary">
            {data.overall_ready
              ? <ShieldCheck size={14} className="text-emerald-700" />
              : <AlertTriangle size={14} className="text-amber-700" />}
            <span className={`text-[11px] font-bold uppercase tracking-wider ${data.overall_ready ? "text-emerald-800" : "text-amber-800"}`}>
              {data.overall_label}
            </span>
            <span className="text-[10px] text-slate-500 ml-auto tabular-nums" data-testid="readiness-effective-date">
              Diperiksa untuk {fmtTanggalID(data.effective_date || data.as_of)}{data.timezone ? ` · ${data.timezone}` : ""} · {data.ready_count}/{data.total_checks} siap
            </span>
          </div>
          <ul className="divide-y divide-slate-100">
            {(data.checks || []).map((c) => {
              const m = STATUS_META[c.status] || STATUS_META.incomplete;
              return (
                <li key={c.key} className="px-3 py-2 flex items-start gap-2 flex-wrap" data-testid={`readiness-check-${c.key}`}>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-semibold uppercase tracking-wider shrink-0 ${m.cls}`}
                    data-testid={`readiness-status-${c.key}`}>
                    <m.Icon size={10} /> {m.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold text-slate-800">{c.label}</div>
                    <div className="text-[10px] text-slate-500">{c.detail}</div>
                    {!canWrite && c.status !== "ready" && (
                      <div className="text-[10px] text-slate-400 italic mt-0.5">
                        Anda memiliki akses lihat — hubungi admin/HR dengan akses Pengaturan Kehadiran untuk melengkapi.
                      </div>
                    )}
                  </div>
                  {c.status !== "ready" && canWrite && (
                    <button type="button" onClick={() => onGoSection?.(c.fix_section)} data-testid={`readiness-fix-${c.key}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold shrink-0">
                      Atur <ArrowRight size={10} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
